/**
 * Static reference content for Irish grammar concepts, shown in the "what does this mean?" explainer
 * modal. This does not change per record, so it lives in JS (not the backend field schema, which only
 * carries the short per-field description/example used for the form UI and tooltips).
 *
 * Each entry is a set of slides so the modal can step through longer explanations and the "types"
 * (e.g. first vs second conjugation, the noun declensions).
 */
import type { ReactNode } from "react"
import { pronunciationGuide } from "./pronunciation_guide"
import { irregularVerbsSlide } from "./irregular_verbs"

export interface GrammarExample {
  /** The Irish form, often a transformation like "rith → ritheann". */
  irish: string
  english: string
}

export interface GrammarSlide {
  heading: string
  /** Prose explanation. Optional when the slide is carried entirely by rich `content`. */
  body?: string
  examples?: GrammarExample[]
  /** Arbitrary rich content (tables, media) rendered under the body. Lets the config carry JSX so
   *  reference modals can be rich, e.g. the pronunciation charts. */
  content?: ReactNode
  /** Jump-to card for the intro slide. Slides with `nav` become a table of contents (icon + label +
   *  a one-line "what it is" + a short worked example) that jumps straight to the section. */
  nav?: GrammarSlideNav
  /** An optional keep-it-simple "Grug tip" (in Grug's voice), shown below the content. */
  tip?: string
}

export interface GrammarSlideNav {
  label: string
  /** One line on what the concept is. */
  description?: string
  /** A short worked example, e.g. "bord -> mbord". */
  example?: string
  /** A left-aligned icon (JSX). */
  icon?: ReactNode
}

export interface GrammarEntry {
  title: string
  slides: GrammarSlide[]
  /** Render all slides stacked on one page instead of paging through them. Best for short, prose
   *  entries; leave off for long ones with big chart tables (e.g. the pronunciation guide). */
  singlePage?: boolean
}

export const GRAMMAR_GLOSSARY: Record<string, GrammarEntry> = {
  // The pronunciation guide is rich JSX (chart tables), authored in its own .tsx file.
  pronunciation: pronunciationGuide,

  gender: {
    title: "Grammatical gender",
    singlePage: true,
    slides: [
      {
        heading: "Masculine or feminine",
        tip: "grug not guess gender from meaning. grug learn gender WITH the word, like 'an bhean' as one lump. two fact become one.",
        body: "Every Irish noun is either masculine or feminine. There's no neuter 'it'. Gender rarely follows meaning, so it's learned along with the word. It matters because it changes the words around the noun.",
        examples: [
          { irish: "an cat", english: "the cat (masculine)" },
          { irish: "an bhean", english: "the woman (feminine)" },
        ],
      },
      {
        heading: "Spelling often tells you the gender",
        tip: "grug not wait to know every gender before speak. grug use word today, fix gender later. daily talk make fluent, not perfect grammar.",
        body: "Gender rarely follows meaning, but it often follows spelling: the word's ending is a strong hint. The diminutive ending -ín is masculine, so cailín ('girl') is masculine even though it means a girl. Endings like -óg and -eog are usually feminine. Don't let a surprise like this stop you: fluency comes from daily use, and gender is easy to fix as you go.",
        examples: [
          { irish: "cailín", english: "girl (masculine, from the -ín ending)" },
          { irish: "fuinneog", english: "window (feminine, from the -eog ending)" },
        ],
      },
      {
        heading: "How gender shows up",
        body: "After the singular article 'an', a feminine noun is lenited (a 'h' is added to its first consonant) while a masculine noun isn't. Adjectives and the genitive also change with gender.",
        examples: [
          { irish: "an fhuinneog", english: "the window (feminine, lenited)" },
          { irish: "an bord", english: "the table (masculine, unchanged)" },
        ],
      },
    ],
  },

  conjugation: {
    title: "Verb conjugation",
    singlePage: true,
    slides: [
      {
        heading: "What conjugation means",
        tip: "grug not learn every ending day one. grug learn one verb good (rith), pattern stick in head, other verbs follow same path.",
        body: "Irish verbs change their endings for tense and person. Almost every verb belongs to one of two conjugations, plus a small set of irregular verbs. The conjugation tells you which endings to use.",
        examples: [{ irish: "rith → ritheann sé", english: "run → he runs" }],
      },
      {
        heading: "First conjugation",
        body: "Usually one-syllable verbs. In the present tense they take -ann after a broad ending, or -eann after a slender one.",
        examples: [
          { irish: "mol → molann", english: "praise → praises" },
          { irish: "bris → briseann", english: "break → breaks" },
        ],
      },
      {
        heading: "Second conjugation",
        body: "Usually two-syllable verbs, often ending in -igh or -ir. In the present they take -aíonn or -íonn, and are often syncopated (a vowel is dropped).",
        examples: [
          { irish: "ceannaigh → ceannaíonn", english: "buy → buys" },
          { irish: "imir → imríonn", english: "play → plays" },
        ],
      },
      irregularVerbsSlide,
    ],
  },

  genitive: {
    title: "The genitive case",
    singlePage: true,
    slides: [
      {
        heading: "The 'of' form",
        body: "The genitive is the form a noun takes to show possession or relationship, where English uses 's or 'of'. Irish changes the noun itself instead of adding a word.",
        examples: [{ irish: "teach → doras an tí", english: "house → the door of the house" }],
      },
      {
        heading: "When it's used",
        body: "Use the genitive after another noun, after compound prepositions, and after verbal nouns.",
        examples: [
          { irish: "hata an fhir", english: "the man's hat" },
          { irish: "i lár an bhaile", english: "in the middle of the town" },
        ],
      },
    ],
  },

  declension: {
    title: "Noun declensions",
    singlePage: true,
    slides: [
      {
        heading: "Five patterns",
        body: "Irish nouns are grouped into five declensions. The declension decides how a noun forms its genitive singular and its plural, similar to English having regular 'cats' but irregular 'mice'.",
      },
      {
        heading: "First declension",
        body: "All masculine, ending in a broad consonant. The genitive singular is made slender, often by inserting an 'i'.",
        examples: [{ irish: "bád → báid", english: "boat → of a boat" }],
      },
      {
        heading: "Second declension",
        body: "Mostly feminine, ending in a consonant. The genitive singular usually adds -e and is made slender.",
        examples: [{ irish: "bróg → bróige", english: "shoe → of a shoe" }],
      },
    ],
  },

  plurals: {
    title: "Plurals",
    slides: [
      {
        heading: "Making nouns plural",
        body: "Irish forms plurals in several ways depending on the noun: by making the ending slender, or by adding endings like -a, -í, -anna, or -tha. The plural is learned with the noun.",
        examples: [
          { irish: "cat → cait", english: "cat → cats" },
          { irish: "úll → úlla", english: "apple → apples" },
        ],
      },
    ],
  },

  verbal_noun: {
    title: "Verbal noun",
    slides: [
      {
        heading: "A verb acting as a noun",
        body: "The verbal noun is the form of a verb used like a noun, close to the English '-ing' form. It's used with 'ag' to make the continuous tenses.",
        examples: [
          { irish: "Tá mé ag rith", english: "I am running" },
          { irish: "Is maith liom léamh", english: "I like reading" },
        ],
      },
    ],
  },

  verbal_adjective: {
    title: "Verbal adjective",
    slides: [
      {
        heading: "The '-ed' form",
        body: "The verbal adjective is like the English past participle ('-ed'). It's usually formed by adding -te or -ta and describes something the action has been done to.",
        examples: [
          { irish: "déan → déanta", english: "do → done" },
          { irish: "rith → rite", english: "run → finished" },
        ],
      },
    ],
  },
}

// Maps a codex grammar field key to its glossary entry (several fields share one concept).
export const FIELD_TO_GLOSSARY: Record<string, string> = {
  gender: "gender",
  declension: "declension",
  genitive_singular: "genitive",
  genitive_plural: "genitive",
  nominative_plural: "plurals",
  conjugation_class: "conjugation",
  verbal_noun: "verbal_noun",
  verbal_adjective: "verbal_adjective",
}

/** Resolves a field key (or glossary key) to its grammar entry, if one exists. */
export const lookupGrammar = (termKey?: string | null): GrammarEntry | undefined => {
  if (!termKey) return undefined
  return GRAMMAR_GLOSSARY[FIELD_TO_GLOSSARY[termKey] ?? termKey]
}
